import React from 'react';
import { Shield, ShieldCheck, ShieldX } from 'lucide-react';
import { Badge, Button, Textarea } from '../../components/ui';
import type { Snapshot } from '../../generated/api/types.gen.js';
import { formatDate } from './stepDebugOutput.utils.js';

interface ApprovalSectionProps {
  approval:
    | { status: 'pending' | 'approved' | 'rejected'; approvedAt?: string; snapshot_id?: number; notes?: string }
    | undefined;
  selectedSnapshot: Snapshot;
  onApprove?: (snapshotId: number) => void;
  onReject?: (notes: string) => void;
  rejectNotes: string;
  setRejectNotes: (v: string) => void;
  showRejectInput: boolean;
  setShowRejectInput: (v: boolean) => void;
}

export const ApprovalSection: React.FC<ApprovalSectionProps> = ({
  approval,
  selectedSnapshot,
  onApprove,
  onReject,
  rejectNotes,
  setRejectNotes,
  showRejectInput,
  setShowRejectInput,
}) => {
  const status = approval?.status ?? 'pending';

  return (
    <div className="border-t pt-3 mt-1 px-1">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-heading">Approval</span>
        <ApprovalBadge status={status} />
      </div>

      {/* Show approved snapshot info */}
      {status === 'approved' && approval?.approvedAt && (
        <div className="text-[10px] text-muted mb-2">
          Approved snapshot: {formatDate(approval.approvedAt)}
          {approval.snapshot_id != null && ` (ID: ${approval.snapshot_id})`}
        </div>
      )}

      {/* Show rejection notes */}
      {status === 'rejected' && approval?.notes && (
        <div className="text-[10px] text-danger mb-2">Rejection notes: {approval.notes}</div>
      )}

      <div className="flex items-center gap-2">
        {onApprove && (
          <Button variant="success" size="sm" onClick={() => onApprove(selectedSnapshot.id)}>
            <ShieldCheck size={12} /> Approve
          </Button>
        )}
        {onReject && !showRejectInput && (
          <Button variant="danger" size="sm" onClick={() => setShowRejectInput(true)}>
            <ShieldX size={12} /> Reject
          </Button>
        )}
      </div>

      {showRejectInput && onReject && (
        <div className="flex items-center gap-2 mt-2">
          <Textarea
            className="flex-1 text-xs!"
            aria-label="Rejection notes"
            rows={2}
            placeholder="Rejection notes..."
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
          />
          <div className="flex flex-col gap-1">
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                onReject(rejectNotes);
                setShowRejectInput(false);
                setRejectNotes('');
              }}
            >
              Confirm
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowRejectInput(false);
                setRejectNotes('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

function ApprovalBadge({ status }: { status: 'pending' | 'approved' | 'rejected' }) {
  if (status === 'approved') {
    return (
      <Badge variant="success" icon={<ShieldCheck size={10} />}>
        Approved
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge variant="danger" icon={<ShieldX size={10} />}>
        Rejected
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" icon={<Shield size={10} />}>
      Pending
    </Badge>
  );
}
